import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { Layer } from "../_overlay/Layer.js";
import { useAnchoredPosition } from "../_overlay/useAnchoredPosition.js";
/* empty css             */
import { Fragment, cloneElement, useCallback, useEffect, useId, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Dropdown/Dropdown.tsx
function refOf(element) {
	if ("ref" in element.props) return element.props.ref;
	return element.ref;
}
function setRef(ref, value) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
/** The trigger with Dropdown's props merged in (a component, so its handlers are JSX props). */
function Trigger({ element, ...props }) {
	return cloneElement(element, props);
}
/**
* A menu button: the trigger opens a menu of actions (APG menu button). Arrow keys,
* Home, End and typing a letter move through the items; Enter or Space picks one;
* Escape closes it and returns focus to the trigger.
*/
function Dropdown({ trigger, items, sections, align = "start", width, open: openProp, defaultOpen = false, onOpenChange, className }) {
	const [openState, setOpenState] = useState(defaultOpen);
	const open = openProp ?? openState;
	const setOpen = useCallback((next) => {
		if (openProp === void 0) setOpenState(next);
		onOpenChange?.(next);
	}, [openProp, onOpenChange]);
	const id = useId();
	const triggerId = trigger.props.id ?? `${id}-trigger`;
	const menuId = `${id}-menu`;
	const headerId = `${id}-header`;
	const triggerRef = useRef(null);
	const menuRef = useRef(null);
	const pendingFocus = useRef(defaultOpen ? "first" : null);
	const { setAnchor, setFloating, style, side, isPositioned } = useAnchoredPosition({
		open,
		side: "bottom",
		align
	});
	const ownRef = refOf(trigger);
	const setTrigger = useCallback((element) => {
		triggerRef.current = element;
		setAnchor(element);
		setRef(ownRef, element);
	}, [setAnchor, ownRef]);
	const resolvedSections = sections ?? [{ items: items ?? [] }];
	const header = resolvedSections.find((section) => section.header != null)?.header;
	const menuItems = () => Array.from(menuRef.current?.querySelectorAll("[role=\"menuitem\"]") ?? []);
	useEffect(() => {
		if (!open || !isPositioned || !pendingFocus.current) return;
		const all = menuItems();
		(pendingFocus.current === "first" ? all[0] : all.at(-1))?.focus();
		pendingFocus.current = null;
	}, [open, isPositioned]);
	const openMenu = (focus) => {
		pendingFocus.current = focus;
		if (!open) setOpen(true);
	};
	const close = (returnFocus) => {
		setOpen(false);
		if (returnFocus) triggerRef.current?.focus();
	};
	const handleTriggerKeyDown = (event) => {
		trigger.props.onKeyDown?.(event);
		if (event.defaultPrevented) return;
		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			event.preventDefault();
			openMenu(event.key === "ArrowDown" ? "first" : "last");
		}
	};
	const handleMenuKeyDown = (event) => {
		const all = menuItems();
		const at = all.indexOf(document.activeElement);
		const focusAt = (index) => all[(index + all.length) % all.length]?.focus();
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				focusAt(at + 1);
				break;
			case "ArrowUp":
				event.preventDefault();
				focusAt(at === -1 ? -1 : at - 1);
				break;
			case "Home":
				event.preventDefault();
				focusAt(0);
				break;
			case "End":
				event.preventDefault();
				focusAt(-1);
				break;
			case "Escape":
				event.preventDefault();
				close(true);
				break;
			case "Tab":
				close(true);
				break;
			default: if (event.key.length === 1 && /\S/.test(event.key) && !event.ctrlKey && !event.metaKey) {
				const letter = event.key.toLowerCase();
				[...all.slice(at + 1), ...all.slice(0, at + 1)].find((item) => item.textContent?.trim().toLowerCase().startsWith(letter))?.focus();
			}
		}
	};
	const pick = (item) => {
		if (item.disabled) return;
		close(true);
		item.onClick?.();
	};
	const widthStyle = width === void 0 ? void 0 : { inlineSize: typeof width === "number" ? `${width}px` : width };
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Trigger, {
		element: trigger,
		ref: setTrigger,
		id: triggerId,
		onClick: (event) => {
			trigger.props.onClick?.(event);
			if (event.defaultPrevented) return;
			if (open) close(false);
			else openMenu("first");
		},
		onKeyDown: handleTriggerKeyDown,
		"aria-haspopup": "menu",
		"aria-expanded": open,
		"aria-controls": open ? menuId : void 0
	}), open && /* @__PURE__ */ jsxs(Layer, {
		kind: "dropdown",
		ref: setFloating,
		insideRefs: [triggerRef],
		onDismiss: () => close(false),
		className: cx("ds-menu", className),
		"data-side": side,
		style: {
			...style,
			...widthStyle,
			opacity: isPositioned ? void 0 : 0
		},
		children: [header != null && /* @__PURE__ */ jsx("div", {
			id: headerId,
			className: "ds-menu__header",
			children: header
		}), /* @__PURE__ */ jsx("div", {
			ref: menuRef,
			id: menuId,
			role: "menu",
			tabIndex: -1,
			"aria-labelledby": triggerId,
			"aria-describedby": header != null ? headerId : void 0,
			className: "ds-menu__list",
			onKeyDown: handleMenuKeyDown,
			children: resolvedSections.map((section, sectionIndex) => {
				const Group = section.label != null ? "div" : Fragment;
				const groupProps = section.label != null ? {
					role: "group",
					"aria-labelledby": `${id}-section-${sectionIndex}`
				} : {};
				return /* @__PURE__ */ jsxs(Fragment, { children: [
					sectionIndex > 0 && /* @__PURE__ */ jsx("div", {
						role: "separator",
						className: "ds-menu__separator"
					}),
					section.label != null && /* @__PURE__ */ jsx("div", {
						id: `${id}-section-${sectionIndex}`,
						role: "presentation",
						className: "ds-menu__section-label",
						children: section.label
					}),
					/* @__PURE__ */ jsx(Group, {
						...groupProps,
						children: section.items.map((item, itemIndex) => /* @__PURE__ */ jsxs("button", {
							type: "button",
							role: "menuitem",
							tabIndex: -1,
							"aria-disabled": item.disabled || void 0,
							className: cx("ds-menu__item", item.danger && "ds-menu__item--danger", item.disabled && "ds-menu__item--disabled"),
							onClick: () => pick(item),
							onPointerMove: (event) => {
								if (event.pointerType !== "touch") event.currentTarget.focus();
							},
							children: [item.icon != null && /* @__PURE__ */ jsx("span", {
								className: "ds-menu__icon",
								"aria-hidden": "true",
								children: typeof item.icon === "string" ? /* @__PURE__ */ jsx(Icon, { name: item.icon }) : item.icon
							}), /* @__PURE__ */ jsx("span", {
								className: "ds-menu__label",
								children: item.label
							})]
						}, item.key ?? itemIndex))
					})
				] }, sectionIndex);
			})
		})]
	})] });
}
//#endregion
export { Dropdown };

//# sourceMappingURL=Dropdown.js.map